/**
 * Wave A · Mobile resilience pass.
 *
 * MobileResilient — wrapper that makes any lazy page survive bad connectivity
 * and render errors on mobile.
 *
 * Features:
 *   1. ErrorBoundary that catches render errors and shows PageErrorState.
 *   2. Offline detection via navigator.onLine + online/offline events →
 *      shows OfflineBanner above the page content.
 *   3. Suspense for lazy-loaded page boundaries.
 *   4. Timeout watchdog: if Suspense hasn't resolved in 8 seconds, the
 *      Promise is considered stale and we surface an error state with retry.
 *      (Implemented via a custom hook that races a timer against a mounted flag.)
 *
 * Usage in router.tsx:
 *   <MobileResilient>
 *     <SomeLazyPage />
 *   </MobileResilient>
 *
 * Props:
 *   children         — page element to wrap
 *   loadingFallback  — custom skeleton (default: <PageSkeleton />)
 *   errorFallback    — custom error UI (receives error + reset; default: <PageErrorState />)
 */

import {
  Component,
  Suspense,
  useEffect,
  useState,
  type ReactNode,
  type ErrorInfo,
} from 'react';
import { PageSkeleton } from './PageSkeleton';
import { PageErrorState } from './PageErrorState';
import { TimedOutState } from './TimedOutState';
// OfflineBanner is intentionally NOT imported here — it is rendered once
// by AppShell (which wraps all pages) so we never get a duplicate banner
// on mobile. MobileResilient handles resilience (retry, timeout, errors);
// the offline UI layer is AppShell's responsibility.

// ---------------------------------------------------------------------------
// Timeout watchdog — fires after 8s if Suspense boundary hasn't resolved.
// ---------------------------------------------------------------------------

const TIMEOUT_MS = 8_000;

interface TimeoutWatchdogProps {
  children: ReactNode;
  /** Called when 8s elapses without rendering a non-fallback child. */
  onTimeout: () => void;
}

/**
 * Renders children immediately and starts an 8-second timer.
 * If the component unmounts (meaning Suspense resolved), the timer is cleared.
 * If 8 seconds pass without unmount, `onTimeout` fires.
 */
function TimeoutWatchdog({ children, onTimeout }: TimeoutWatchdogProps) {
  useEffect(() => {
    const tid = window.setTimeout(onTimeout, TIMEOUT_MS);
    return () => window.clearTimeout(tid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// SuspenseWithTimeout — Suspense that activates watchdog via the fallback.
// ---------------------------------------------------------------------------

interface SuspenseWithTimeoutProps {
  fallback: ReactNode;
  children: ReactNode;
  onTimeout: () => void;
}

function SuspenseWithTimeout({
  fallback,
  children,
  onTimeout,
}: SuspenseWithTimeoutProps) {
  return (
    <Suspense
      fallback={
        <TimeoutWatchdog onTimeout={onTimeout}>{fallback}</TimeoutWatchdog>
      }
    >
      {children}
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// ErrorBoundary implementation.
// ---------------------------------------------------------------------------

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: (error: Error | null, reset: () => void) => ReactNode;
}

class MobileErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console in dev; the global error hooks in main.tsx handle prod.
    if (process.env.NODE_ENV !== 'production') {
      console.error('[MobileResilient] Render error:', error, info);
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      return this.props.fallback(this.state.error, this.reset);
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Custom error fallback type.
// ---------------------------------------------------------------------------

export interface ErrorFallbackProps {
  error: Error | null;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// MobileResilient — main export.
// ---------------------------------------------------------------------------

export interface MobileResilientProps {
  children: ReactNode;
  /** Override the loading skeleton. */
  loadingFallback?: ReactNode;
  /** Override the error UI. Receives error + reset fn. */
  errorFallback?: (props: ErrorFallbackProps) => ReactNode;
}

/**
 * Wrapper component that adds offline banner, Suspense, error boundary,
 * and an 8-second loading timeout watchdog.
 */
export function MobileResilient({
  children,
  loadingFallback,
  errorFallback,
}: MobileResilientProps) {
  const [timedOut, setTimedOut] = useState(false);
  const [timeoutKey, setTimeoutKey] = useState(0);

  function handleTimeout() {
    setTimedOut(true);
  }

  function handleRetry() {
    setTimedOut(false);
    setTimeoutKey((k) => k + 1);
    // Force a fresh chunk fetch attempt by reloading if offline
    if (!navigator.onLine) {
      window.location.reload();
    }
  }

  const defaultLoadingFallback = loadingFallback ?? <PageSkeleton />;

  // Locale-aware defaults are resolved inside PageErrorState/TimedOutState;
  // we omit the string props here so those components use their own locale-
  // aware defaults instead of hardcoding PT-BR at the call site.
  const defaultErrorFallback = ({ error, reset }: ErrorFallbackProps) => (
    <PageErrorState
      onRetry={() => {
        reset();
        handleRetry();
      }}
      errorDetail={error?.message}
      iconVariant={navigator.onLine ? 'error' : 'offline'}
    />
  );

  const resolvedErrorFallback = errorFallback ?? defaultErrorFallback;

  if (timedOut) {
    // Locale-aware defaults resolved inside TimedOutState; no PT-BR hardcoded here.
    return <TimedOutState onRetry={handleRetry} />;
  }

  return (
    <>
      <MobileErrorBoundary
        key={timeoutKey}
        fallback={(error, reset) => resolvedErrorFallback({ error, reset })}
      >
        <SuspenseWithTimeout
          fallback={defaultLoadingFallback}
          onTimeout={handleTimeout}
        >
          {children}
        </SuspenseWithTimeout>
      </MobileErrorBoundary>
    </>
  );

}
